import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Lock, Upload, ChevronDown, ChevronUp, Palette, MessageSquare, Type, Image as ImageIcon, Loader2, ArrowRight } from 'lucide-react';
import PortalBrandPreview from './PortalBrandPreview';

/**
 * Per-portal branding overrides dialog. Ultimate-effective only (real plan or
 * active 7-day trial). Empty fields fall back to the workspace baseline.
 */
export default function PortalBrandingDialog({ portal, canEdit, onClose, onSaved }) {
    const open = !!portal;
    const [expanded, setExpanded] = useState(true);
    const [form, setForm] = useState({ portal_name_string: '', portal_accent_hex: '', portal_welcome_message: '', portal_logo_url: null });
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const logoInput = useRef(null);

    useEffect(() => {
        if (portal) {
            setForm({
                portal_name_string: portal.portal_name_string || '',
                portal_accent_hex: portal.portal_accent_hex || '',
                portal_welcome_message: portal.portal_welcome_message || '',
                portal_logo_url: portal.portal_logo_url || null,
            });
            setExpanded(true);
        }
    }, [portal]);

    if (!open) return null;

    const previewAccent = form.portal_accent_hex || '#FF6B35';

    const onLogoPick = async (file) => {
        if (!file) return;
        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
            toast.error('Only PNG or JPEG accepted');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Logo must be under 5MB');
            return;
        }
        setUploadingLogo(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post(`/portals/${portal.id}/branding/logo`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setForm((f) => ({ ...f, portal_logo_url: data.portal_logo_url }));
            toast.success('Logo uploaded');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Upload failed');
        } finally {
            setUploadingLogo(false);
            if (logoInput.current) logoInput.current.value = '';
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/portals/${portal.id}/branding`, {
                portal_name_string: form.portal_name_string,
                portal_accent_hex: form.portal_accent_hex,
                portal_welcome_message: form.portal_welcome_message,
            });
            toast.success('Portal branding saved');
            onSaved?.(data.portal);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="portal-branding-dialog" className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Client-specific branding · {portal.title}</DialogTitle>
                    <DialogDescription>
                        These overrides apply only to this portal link. Any field left empty falls back to your workspace baseline.
                    </DialogDescription>
                </DialogHeader>

                {!canEdit ? (
                    <div
                        data-testid="portal-branding-locked"
                        className="border border-dashed border-[#FF6B35]/40 bg-[#FF6B35]/5 p-6 flex items-start gap-3"
                    >
                        <Lock className="h-4 w-4 text-[#FF6B35] shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium">Per-portal branding overrides are Ultimate-only.</p>
                            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                                Ship a different logo, color, and welcome message for every client link. Upgrade to Ultimate or start the free 7-day trial from your Brand Settings panel.
                            </p>
                            <Button
                                data-testid="portal-branding-upgrade-btn"
                                onClick={() => { window.location.href = '/pricing'; }}
                                className="mt-4 bg-[#FF6B35] hover:bg-[#E55C2A] text-white"
                                size="sm"
                            >
                                View upgrade options <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Collapsible block */}
                        <button
                            type="button"
                            onClick={() => setExpanded((e) => !e)}
                            data-testid="portal-branding-toggle"
                            className="w-full flex items-center justify-between border border-border px-4 py-3 hover:border-[#FF6B35]/50 transition-colors"
                        >
                            <span className="text-xs uppercase tracking-[0.2em] text-[#FF6B35]">Customize Branding for This Client</span>
                            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>

                        {expanded && (
                            <div className="grid md:grid-cols-5 gap-6">
                                {/* Form */}
                                <div className="md:col-span-3 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2 text-xs">
                                                <ImageIcon className="h-3 w-3 text-[#FF6B35]" /> Client logo
                                            </Label>
                                            <div
                                                onClick={() => logoInput.current?.click()}
                                                data-testid="portal-logo-picker"
                                                className="h-16 border border-dashed border-border grid place-items-center bg-background/50 hover:border-[#FF6B35]/60 cursor-pointer"
                                            >
                                                {form.portal_logo_url ? (
                                                    <img src={form.portal_logo_url} alt="" className="h-10 max-w-[80%] object-contain" />
                                                ) : (
                                                    <div className="text-center text-[10px] text-muted-foreground">
                                                        <Upload className="h-3 w-3 mx-auto mb-1 text-[#FF6B35]" /> PNG / JPEG · 5MB
                                                    </div>
                                                )}
                                            </div>
                                            <input
                                                ref={logoInput}
                                                data-testid="portal-logo-input"
                                                type="file"
                                                accept="image/png,image/jpeg"
                                                className="hidden"
                                                onChange={(e) => onLogoPick(e.target.files?.[0])}
                                            />
                                            {uploadingLogo && <p className="text-[10px] text-muted-foreground">Uploading…</p>}
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2 text-xs">
                                                <Type className="h-3 w-3 text-[#FF6B35]" /> Brand name (this portal)
                                            </Label>
                                            <Input
                                                data-testid="portal-brand-name-input"
                                                placeholder="Nike Media Delivery"
                                                value={form.portal_name_string}
                                                maxLength={80}
                                                onChange={(e) => setForm((f) => ({ ...f, portal_name_string: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2 text-xs">
                                                <Palette className="h-3 w-3 text-[#FF6B35]" /> Accent color
                                            </Label>
                                            <div className="flex gap-2">
                                                <input
                                                    data-testid="portal-accent-picker"
                                                    type="color"
                                                    value={form.portal_accent_hex || '#FF6B35'}
                                                    onChange={(e) => setForm((f) => ({ ...f, portal_accent_hex: e.target.value.toUpperCase() }))}
                                                    className="h-9 w-12 border border-border cursor-pointer bg-transparent"
                                                    aria-label="Pick accent color"
                                                />
                                                <Input
                                                    data-testid="portal-accent-hex-input"
                                                    value={form.portal_accent_hex}
                                                    maxLength={7}
                                                    placeholder="Inherit workspace"
                                                    onChange={(e) => setForm((f) => ({ ...f, portal_accent_hex: e.target.value.toUpperCase() }))}
                                                    className="font-mono flex-1 text-xs"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2 col-span-2 md:col-span-1">
                                            <Label className="flex items-center gap-2 text-xs">
                                                <MessageSquare className="h-3 w-3 text-[#FF6B35]" /> Client welcome
                                            </Label>
                                            <Textarea
                                                data-testid="portal-welcome-input"
                                                rows={2}
                                                placeholder="Inherit workspace default"
                                                value={form.portal_welcome_message}
                                                maxLength={500}
                                                onChange={(e) => setForm((f) => ({ ...f, portal_welcome_message: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground border-l-2 border-[#FF6B35] pl-3 leading-relaxed">
                                        Leave any field blank to inherit your workspace baseline. The client-facing portal will only ever see the merged result — never internal metadata.
                                    </p>
                                </div>

                                {/* Live preview */}
                                <div className="md:col-span-2 space-y-2">
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-l-2 border-[#FF6B35] pl-3">
                                        Client sees
                                    </p>
                                    <PortalBrandPreview
                                        testId="portal-live-preview"
                                        accent={previewAccent}
                                        logoUrl={form.portal_logo_url}
                                        brandName={form.portal_name_string}
                                        welcomeMessage={form.portal_welcome_message}
                                        canWhiteLabel={true}
                                        canGreeting={true}
                                        portalTitle={portal.title}
                                        ownerName="You"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="portal-branding-cancel">Cancel</Button>
                    {canEdit && (
                        <Button
                            data-testid="portal-branding-save"
                            onClick={save}
                            disabled={saving}
                            className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white"
                        >
                            {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>) : 'Save overrides'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
